import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { Parameters, RequestBody, Response } from '../../../../types/typeHelper';
import { ERROR_DIALOG } from '../../../constants/errorDialogConstants';
import { useHandleQueryError } from '../../../helper/errorMessages';

import { orderUpdated } from '../../../slices/quickOrdering/quickOrderingSlice';
import { useApiClient } from '../client';

export enum OrderClientQueryKeys {
  GET_ORDER = 'GET_ORDER',
}

export type ApiFetchOrderData = Response<'/chat/quick-order/orders/{order_id}', 'get'>;

export function useGetOrderQuery(path: Parameters<'/chat/quick-order/orders/{order_id}', 'get'>['path']) {
  const client = useApiClient();
  const handleQueryError = useHandleQueryError();
  return useQuery({
    queryKey: [OrderClientQueryKeys.GET_ORDER, path],
    enabled: !!path.order_id,
    queryFn: async () => {
      const { data, error } = await client.GET('/chat/quick-order/orders/{order_id}', {
        params: {
          path,
        },
      });
      handleQueryError({
        errorDialog: ERROR_DIALOG.NONE,
        // @ts-expect-error mistatch between openapi-fetch error and backend error types
        error,
      });
      return data;
      // Note: Error throwing handled automatically via middleware
    },
  });
}

export type ApiUpdateOrderBody = RequestBody<'/chat/quick-order/orders/{order_id}', 'patch'>;

export function useUpdateOrderMutation() {
  const queryClient = useQueryClient();
  const client = useApiClient();
  return useMutation({
    mutationFn: async ({
      params,
      body,
    }: {
      params: Parameters<'/chat/quick-order/orders/{order_id}', 'patch'>;
      body: RequestBody<'/chat/quick-order/orders/{order_id}', 'patch'>;
    }) => {
      return client.PATCH('/chat/quick-order/orders/{order_id}', {
        params,
        body,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [OrderClientQueryKeys.GET_ORDER] });
    },
  });
}

export type ApiSubmitOrder = RequestBody<'/chat/quick-order/orders/{order_id}/checkout', 'put'>;
export type ApiSubmitOrderResponse = Response<'/chat/quick-order/orders/{order_id}/checkout', 'put'>;
export type SubmitOrderMutation = ReturnType<typeof useSubmitOrderMutation>;

export function useSubmitOrderMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const client = useApiClient();
  const mutation = useMutation({
    mutationFn: async ({ params }: { params: Parameters<'/chat/quick-order/orders/{order_id}/checkout', 'put'> }) => {
      return client.PUT('/chat/quick-order/orders/{order_id}/checkout', {
        params,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [OrderClientQueryKeys.GET_ORDER] });
    },
  });
  useEffect(() => {
    if (!mutation.data?.data) {
      return;
    }
    dispatch(orderUpdated({ orderId: mutation.data.data?.id, order: mutation.data.data }));
  }, [mutation.data, dispatch]);

  return mutation;
}
